import React, { useEffect, useState } from "react";
import axios from "axios";
import { Row, Col, Card, Spinner, Modal, Button } from "react-bootstrap";
import IconButton from "components/common/iconButton";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import NewLocation from "./newLocation";
import EditLocation from "./editLocation";
import { useNavigate } from "react-router-dom";
import { useLocationRedirect } from "assets/js/common";
import { useAppContext } from "providers/appProvider";
import { getDomainConfig } from "../../../../config/domainConfig";

const Locations = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [relations, setRelations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false); // Toggle for add form
  const [showEditForm, setShowEditForm] = useState(false); // Toggle for edit form
  const [selectedLocation, setSelectedLocation] = useState(null); // Track the selected location for editing
  const [showDeleteModal, setShowDeleteModal] = useState(false); // Toggle for delete modal
  const [locationToDelete, setLocationToDelete] = useState(null); // Location to delete
  const [locationId, setLocationId] = useState(null);
  const [canAddLocation, setCanAddLocation] = useState(false); // State for Add permission
  const [canEditLocation, setCanEditLocation] = useState(false); // State for Edit permission
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { locations, setLocations, changeLocation } = useAppContext();
  const customerId = localStorage.getItem("customerId");
  const [isAsperionSettingEnabled, setIsAsperionSettingEnabled] = useState(false);
  const finLabels = getDomainConfig().financialLabels;
  
  useEffect(() => {
    fetchAsperionSettingsEnable();
  }, []);

  const fetchAsperionSettingsEnable = async () => {
    try {
      const customerId = localStorage.getItem("customerId");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/settingsRoute/asperionSync/${customerId}`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
      setIsAsperionSettingEnabled(response.data?.AsperionSyncEnabled);
    } catch (error) {
      console.error("Error fetching Asperion settings:", error);
    }
  };

  const fetchLocations = async () => {
    setLoading(true);
    try {
      if (!customerId) {
        setError("CustomerId is missing.");
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/locationRoute/all`, {
        params: { customerId },
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setLocations(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch locations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useLocationRedirect("dashboard");

  useEffect(() => {
    const fetchRoleAndClaims = async () => {
      const userId = localStorage.getItem("UserId");
      if (!userId) {
        toast.error(t("userIdMissing"));
        navigate("/dashboard");
        return;
      }

      try {
        const userResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        );
        const roleId = userResponse.data.Role;

        const claimsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/claimRoute/claims`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        );
        const filteredClaims = claimsResponse.data.filter(
          (claim) => claim.RoleId === roleId
        );

        // Check for "create" and "edit" permissions
        const hasAddPermission = filteredClaims.some(
          (claim) => claim.Type === "settings" && claim.Value === "create"
        );
        const hasEditPermission = filteredClaims.some(
          (claim) => claim.Type === "settings" && claim.Value === "edit"
        );

        setCanAddLocation(hasAddPermission);
        setCanEditLocation(hasEditPermission);
      } catch (error) {
        toast.error(t("permissionValidationError"), { toastId: "settings-permission-error" });
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoleAndClaims();
  }, [navigate]);

  const fetchUsers = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/userRoute/users/allLocations`,
        {
          params: { customerId },
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRelations = async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate(`${paths.login}`);
        return;
      }

      if (!customerId) {
        setError("CustomerId is missing.");
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/relationRoute/relations`,
        {
          params: { locationId, customerId },
          headers: {
            // Authorization: token,
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );

      setRelations(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch relations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const location = localStorage.getItem("LocationId");
    setLocationId(location);
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchUsers();
    fetchRelations();
  }, [locationId]);

  const handleDeleteLocation = async () => {
    if (!locationToDelete) return;

    const hasUser = users.some(user =>
      user.IsDelete !== true &&
      user.locations?.some(
        loc => loc.LocationId === locationToDelete.LocationId
      )
    );

    const hasRelation = relations.some(
      (relation) => relation.LocationId == locationToDelete.LocationId
    );

    if (hasUser || hasRelation) {
      // If there are users or relations associated, don't allow deletion and show a warning message t("locationDeleteWarningMessage")
      toast.error(t("deleteBranchError"), {
        position: "bottom-left",
        autoClose: 5000,
      });
      setShowDeleteModal(false);
      return; // Prevent deletion
    }

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/locationRoute/delete/${locationToDelete.LocationId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      fetchLocations();

      setShowDeleteModal(false); // Close the modal after successful deletion

      toast.success(t("branchDeletedSuccessMessage"), {
        position: "top-right",
        autoClose: 5000,
      });
    } catch (error) {
      toast.error(t("branchDeleteFailed"));
    }
  };

  const openDeleteModal = (location) => {
    setLocationToDelete(location);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setLocationToDelete(null);
    setShowDeleteModal(false);
  };

  const handleAddLocation = () => {
    setShowAddForm(true);
    setShowEditForm(false); // Ensure edit form is hidden
  };

  const handleEditLocation = (location) => {
    setSelectedLocation(location);
    setShowEditForm(true);
    setShowAddForm(false); // Ensure add form is hidden
  };

  const handleFormSuccess = () => {
    setShowAddForm(false);
    setShowEditForm(false); // Hide both forms
    fetchLocations(); // Refresh the location list
  };

  const countryTranslationMap = {
  Nederland: "netherlands"
};

  const handleFormCancel = () => {
    setShowAddForm(false);
    setShowEditForm(false); // Hide both forms
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <p className="text-danger">{error}</p>;
  }

  return (
    <div>
      {showAddForm ? (
        <NewLocation
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      ) : showEditForm && selectedLocation ? (
        <EditLocation
          locationId={selectedLocation.LocationId} // Pass the LocationId
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      ) : (
        <Row className="g-3">
          <Col lg={12} className="d-flex justify-content-end mb-2">
            <IconButton
              iconClassName="fs--2 me-1"
              variant="primary"
              size="sm"
              icon="plus"
              onClick={handleAddLocation}
              disabled={!canAddLocation}
            >
              {t("newBranch")}
            </IconButton>
          </Col>

          {locations.map((location) => (
            <Col key={location.LocationId} md={6} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-bold data-font-colour mb-0">
                      {location.Name}
                    </h6>
                    <div className="d-flex">
                      <IconButton
                        variant="outline-primary"
                        className="me-2"
                        size="sm"
                        onClick={() => handleEditLocation(location)} // Trigger edit form
                        icon="pencil-alt"
                        disabled={!canEditLocation}
                      />
                      <IconButton
                        variant="outline-danger"
                        size="sm"
                        onClick={() => openDeleteModal(location)} // Open delete modal
                        icon="trash-alt"
                      />
                    </div>
                  </div>
                  <p className="mb-1 data-font-size data-font-colour">
                    {location.StreetName}, {location.HouseNumber}{" "}
                    {location.Addition || ""}, {location.PostCode},{" "}
                    {location.City}, {" "}
                    {location.Country
                    ? t(countryTranslationMap[location.Country] || location.Country)
                    : ""}
                  </p>

                  {/* IBAN — NL only */}
                  {!finLabels.showIfsc && (
                    <p className="mb-1 data-font-size data-font-colour">
                      <strong>{t("iban")}:</strong> {location.Iban ?? "--"}
                    </p>
                  )}

                  {/* VAT/BTW — NL only */}
                  {!finLabels.showIfsc && (
                    <p className="mb-1 data-font-size data-font-colour">
                      <strong>{t(finLabels.vat)}:</strong> {location.BTWDetails ?? "--"}
                    </p>
                  )}

                  {/* KvK/CoC — NL only */}
                  {finLabels.showKvk && (
                    <p className="mb-1 data-font-size data-font-colour">
                      <strong>{t("kvkDetails")}:</strong> {location.KvKDetails ?? "--"}
                    </p>
                  )}

                  {/* GST — IN only */}
                  {finLabels.showIfsc && (
                    <p className="mb-1 data-font-size data-font-colour">
                      <strong>{t(finLabels.gst)}:</strong> {location.GstDetails ?? "--"}
                    </p>
                  )}

                  {/* Account Number — IN only */}
                  {finLabels.showAccount && (
                    <p className="mb-1 data-font-size data-font-colour">
                      <strong>{t(finLabels.account)}:</strong> {location.AccountNumber ?? "--"}
                    </p>
                  )}

                  {/* IFSC — IN only */}
                  {finLabels.showIfsc && (
                    <p className="mb-1 data-font-size data-font-colour">
                      <strong>{t(finLabels.ifsc)}:</strong> {location.IfscCode ?? "--"}
                    </p>
                  )}

                  <div className="asperion-details">
                    <p className="mb-1 data-font-size data-font-colour d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <strong>{t("bookkeepingDetails")}:</strong>
                      {isAsperionSettingEnabled && (
                        <span
                          className={`badge rounded-pill badge-subtle-${
                            location.AutoAsperionSyncFlag ? "success" : "secondary"
                          }`}
                        >
                          <span className="badge-dot me-1">●</span>
                          {location.AutoAsperionSyncFlag
                            ? t("autoSyncEnabled")
                            : t("autoSyncDisabled")}
                        </span>
                      )}
                    </p>
                    {/* <div className="asperion-info">
                      <p className="mb-1 data-font-colour">
                        <span className="label">{t("userId")}:</span>{" "}
                        <span className="value">
                          {location.AsperionUserId ?? "--"}
                        </span>
                      </p>
                      <p className="mb-1 data-font-colour">
                        <span className="label">{t("password")}:</span>{" "}
                        <span className="value">
                          {location.AsperionPassword ? "********" : "--"}
                        </span>
                      </p>
                    </div> */}
                    {location.AutoAsperionSyncFlag && (
                      <div className="asperion-info">
                        <p className="mb-1 data-font-size data-font-colour">
                          <span className="label">{t("administrationNumber")}:</span>{" "}
                          <span className="value">
                            {location.AsperionTenantId ?? "--"}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal} backdrop="static">
        <Modal.Header>
          <Modal.Title className="h5 font-bold">
            {t("confirmDeletion")}
          </Modal.Title>
          <Button variant="close" onClick={closeDeleteModal} />
        </Modal.Header>
        <Modal.Body className="data-font-size data-font-colour">
          {t("areYouSureDelete")} <strong>{locationToDelete?.Name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="falcon-default" size="sm" onClick={closeDeleteModal}>
            {t("cancel")}
          </Button>
          <Button variant="primary" size="sm" onClick={handleDeleteLocation}>
            {t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Locations;